import React, { useState } from 'react'
import { Box, Button, CircularProgress, Container, Paper, Stack, TextField, Typography, Alert, Rating, List, ListItem, ListItemText, LinearProgress } from '@mui/material'
import { Navbar } from '../features/navigation/components/Navbar'
import { Footer } from '../features/footer/Footer'
import { axiosi } from '../config/axios'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { selectLoggedInUser } from '../features/auth/AuthSlice'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'

export const AddReviewPage = () => {
  const { productId } = useParams()
  const [formData, setFormData] = useState({
    rating: 5,
    comment: '',
    photos: [],
    video: null
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const loggedInUser = useSelector(selectLoggedInUser)
  const navigate = useNavigate()

  const handleRatingChange = (e, value) => {
    setFormData(prev => ({ ...prev, rating: value }))
  }

  const handleCommentChange = (e) => {
    setFormData(prev => ({ ...prev, comment: e.target.value }))
  }

  const handlePhotoUpload = (e) => {
    const files = e.target.files
    if (files) {
      const newPhotos = Array.from(files).map(f => ({
        name: f.name,
        size: f.size,
        file: f
      }))
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...newPhotos].slice(0, 5)
      }))
    }
  }

  const handleVideoUpload = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setFormData(prev => ({
        ...prev,
        video: {
          name: file.name,
          size: file.size,
          file: file
        }
      }))
    }
  }

  const handleRemovePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }))
  }

  const handleRemoveVideo = () => {
    setFormData(prev => ({ ...prev, video: null }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.comment || formData.comment.length < 10) {
      setMessage({ type: 'error', text: 'Comment must be at least 10 characters' })
      return
    }

    setLoading(true)
    try {
      const submitData = new FormData()
      submitData.append('product', productId)
      submitData.append('rating', formData.rating)
      submitData.append('comment', formData.comment)

      formData.photos.forEach((photo) => {
        submitData.append('photos', photo.file)
      })

      if (formData.video) {
        submitData.append('video', formData.video.file)
      }

      await axiosi.post('/reviews', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${loggedInUser?.token}`
        }
      })

      setMessage({ type: 'success', text: 'Review submitted! It will appear after admin approval.' })
      setTimeout(() => {
        navigate('/products')
      }, 2000)
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Error submitting review'
      })
    } finally {
      setLoading(false)
    }
  }

  const totalSize = (formData.photos.reduce((sum, p) => sum + p.size, 0) + (formData.video?.size || 0)) / (1024 * 1024)

  return (
    <>
      <Navbar />
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
            ⭐ Add Your Review
          </Typography>

          {message.text && (
            <Alert 
              severity={message.type} 
              sx={{ mb: 2 }}
              onClose={() => setMessage({ type: '', text: '' })}
            >
              {message.text}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Rating */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Rate this product</Typography>
                <Rating 
                  value={formData.rating} 
                  onChange={handleRatingChange}
                  size="large"
                />
              </Box>

              {/* Comment */}
              <TextField
                fullWidth
                label="Share your thoughts"
                multiline
                rows={4}
                value={formData.comment}
                onChange={handleCommentChange}
                placeholder="Tell others about your experience with this product..."
                inputProps={{ minLength: 10 }}
                helperText={`${formData.comment.length}/500 characters`}
                error={formData.comment.length > 500}
              />

              {/* Photo Upload */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>📷 Add Photos (Optional)</Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  disabled={formData.photos.length >= 5}
                >
                  Upload Photos (Max 5, {formData.photos.length}/5)
                  <input
                    hidden
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoUpload}
                  />
                </Button>
                
                {formData.photos.length > 0 && (
                  <List sx={{ mt: 2 }}>
                    {formData.photos.map((photo, idx) => (
                      <ListItem
                        key={idx}
                        secondaryAction={
                          <Button
                            edge="end"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleRemovePhoto(idx)}
                          >
                            Remove
                          </Button>
                        }
                      >
                        <ListItemText
                          primary={photo.name}
                          secondary={`${(photo.size / 1024).toFixed(2)} KB`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              {/* Video Upload */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>🎥 Add Video (Optional)</Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  disabled={!!formData.video}
                >
                  Upload Video (Max 50MB)
                  <input
                    hidden
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                  />
                </Button>

                {formData.video && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        {formData.video.name} - {(formData.video.size / (1024 * 1024)).toFixed(2)} MB
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleRemoveVideo}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Reward Info */}
              {(formData.photos.length > 0 || formData.video) && (
                <Box sx={{ p: 2, backgroundColor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                    🎁 Earn a discount coupon when your review is approved!
                  </Typography>
                </Box>
              )}

              {/* File Size Indicator */}
              {totalSize > 0 && (
                <Box>
                  <Typography variant="caption">Total file size: {totalSize.toFixed(2)} MB</Typography>
                  <LinearProgress variant="determinate" value={Math.min(totalSize, 50) * 2} sx={{ mt: 1 }} />
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                    {totalSize > 50 ? '⚠️ File size exceeds limit (max 50MB)' : '✓ Within size limits'}
                  </Typography>
                </Box>
              )}

              <Button
                variant="contained"
                type="submit"
                size="large"
                disabled={loading || !formData.comment || totalSize > 50}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Submitting...' : '✅ Submit Review'}
              </Button>

              <Button
                variant="outlined"
                onClick={() => navigate('/products')}
              >
                Back to Products
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
      <Footer />
    </>
  )
}
